import React, { useEffect, useState } from 'react';
import { Segment, Table } from 'semantic-ui-react';
import styled from 'styled-components';

import TablePaginationMenu from '../../../components/TablePaginationMenu';
import {
  listStandaloneChargeBillingEstates,
  StandaloneChargeBillingEstateDTO
} from '../../../services/standalone-charges';
import { dangerNotification } from '../../../services/notification';

import {
  billingEstateStatusLabelMap,
  initialBillingEstatesFilters,
  initialBillingEstatesSort
} from '../constants';
import {
  BillingEstateItem,
  BillingEstateSortColumn,
  BillingEstatesFiltersState,
  BillingEstatesSortState
} from '../types';
import EstateBillingDetailsModal from './EstateBillingDetailsModal';
import EstateBiometryModal from './EstateBiometryModal';
import EstatesChargesActionsMenu from './EstatesChargesActionsMenu';
import EstatesChargesFiltersBar from './EstatesChargesFiltersBar';
import EstatesChargesSortableHeader from './EstatesChargesSortableHeader';

type Props = {
  onOpenEstateInSentTab: (estateId: string) => void;
  canEditBankAccount?: boolean;
};

const TableWrapper = styled.div`
  overflow: visible;

  @media (max-width: 991px) {
    overflow-x: auto;
    padding-bottom: 120px;
  }
`;

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
});

const formatActivatedAt = (activatedAt?: string | null) => {
  if (!activatedAt) return '-';

  const date = new Date(activatedAt);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('pt-BR');
};

const stopRowOpen = (e: React.SyntheticEvent) => {
  e.stopPropagation();
};

const mapBillingEstate = (estate: StandaloneChargeBillingEstateDTO): BillingEstateItem => ({
  estateId: estate.estateId,
  estateName: estate.estateName,
  activatedAt: estate.activatedAt,
  activeCollaboratorsCount: estate.activeCollaboratorsCount,
  paidChargesCount: estate.paidChargesCount,
  totalMovedAmount: estate.totalMovedAmount,
  ladoBomRevenueAmount: estate.ladoBomRevenueAmount,
  status: estate.status,
  kycToken: estate.kycToken
});

const EstatesChargesList: React.FC<Props> = ({
  onOpenEstateInSentTab,
  canEditBankAccount = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [estates, setEstates] = useState<BillingEstateItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<BillingEstatesFiltersState>(
    initialBillingEstatesFilters
  );
  const [appliedFilters, setAppliedFilters] = useState<BillingEstatesFiltersState>(
    initialBillingEstatesFilters
  );
  const [sort, setSort] = useState<BillingEstatesSortState>(initialBillingEstatesSort);
  const [selectedEstate, setSelectedEstate] = useState<BillingEstateItem | null>(null);
  const [biometryModalOpen, setBiometryModalOpen] = useState(false);
  const [detailsEstateId, setDetailsEstateId] = useState<string | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const loadData = async (
    page = currentPage,
    pageFilters: BillingEstatesFiltersState = appliedFilters,
    pageSort: BillingEstatesSortState = sort
  ) => {
    setIsLoading(true);
    try {
      const response = await listStandaloneChargeBillingEstates({
        page,
        limit: 10,
        name: pageFilters.name || undefined,
        status: pageFilters.status || undefined,
        sortBy: pageSort.sortBy,
        sortOrder: pageSort.sortOrder
      });
      setEstates((response.data || []).map(mapBillingEstate));
      setTotal(response.count || 0);
    } catch (error) {
      dangerNotification('Erro', 'Não foi possível carregar imobiliárias de cobranças.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData(currentPage, appliedFilters, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedFilters, sort]);

  const handleSearch = () => {
    setCurrentPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialBillingEstatesFilters);
    setSort(initialBillingEstatesSort);
    setCurrentPage(1);
    setAppliedFilters(initialBillingEstatesFilters);
  };

  const handleSort = (columnKey: BillingEstateSortColumn) => {
    setSort(prev => {
      if (prev.sortBy === columnKey) {
        return {
          sortBy: columnKey,
          sortOrder: prev.sortOrder === 'ASC' ? 'DESC' : 'ASC'
        };
      }

      return {
        sortBy: columnKey,
        sortOrder: 'ASC'
      };
    });
    setCurrentPage(1);
  };

  const openBiometryModal = (estate: BillingEstateItem) => {
    setSelectedEstate(estate);
    setBiometryModalOpen(true);
  };

  const closeBiometryModal = () => {
    setBiometryModalOpen(false);
    setSelectedEstate(null);
  };

  const openRegistration = (estate: BillingEstateItem) => {
    onOpenEstateInSentTab(estate.estateId);
  };

  const openDetailsModal = (estate: BillingEstateItem) => {
    setDetailsEstateId(estate.estateId);
    setDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setDetailsEstateId(null);
  };

  const openRegistrationFromDetails = (estateId: string) => {
    closeDetailsModal();
    onOpenEstateInSentTab(estateId);
  };

  const openBiometryFromDetails = (estate: BillingEstateItem) => {
    closeDetailsModal();
    openBiometryModal(estate);
  };

  const sortableHeaderProps = {
    activeSortBy: sort.sortBy,
    activeSortOrder: sort.sortOrder,
    onSort: handleSort
  };

  return (
    <>
      <EstatesChargesFiltersBar
        filters={filters}
        setFilters={setFilters}
        onSearch={handleSearch}
        onClear={clearFilters}
      />

      <Segment loading={isLoading}>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Imobiliárias</h3>
        </div>

        <TableWrapper>
          <Table celled striped unstackable selectable style={{ minWidth: 1100 }}>
            <Table.Header>
              <Table.Row>
                <EstatesChargesSortableHeader columnKey="activatedAt" {...sortableHeaderProps}>
                  Data de ativação
                </EstatesChargesSortableHeader>
                <EstatesChargesSortableHeader columnKey="estateName" {...sortableHeaderProps}>
                  Nome da imobiliária
                </EstatesChargesSortableHeader>
                <EstatesChargesSortableHeader
                  columnKey="activeCollaboratorsCount"
                  {...sortableHeaderProps}
                >
                  Colaboradores ativos
                </EstatesChargesSortableHeader>
                <EstatesChargesSortableHeader
                  columnKey="paidChargesCount"
                  {...sortableHeaderProps}
                >
                  Cobranças realizadas
                </EstatesChargesSortableHeader>
                <EstatesChargesSortableHeader
                  columnKey="totalMovedAmount"
                  {...sortableHeaderProps}
                >
                  Total movimentado
                </EstatesChargesSortableHeader>
                <EstatesChargesSortableHeader
                  columnKey="ladoBomRevenueAmount"
                  {...sortableHeaderProps}
                >
                  Receita LadoBom
                </EstatesChargesSortableHeader>
                <EstatesChargesSortableHeader columnKey="status" {...sortableHeaderProps}>
                  Status
                </EstatesChargesSortableHeader>
                <Table.HeaderCell>Ações</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {estates.length === 0 && !isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={8} textAlign="center">
                    Nenhuma imobiliária encontrada.
                  </Table.Cell>
                </Table.Row>
              ) : (
                estates.map(estate => (
                  <Table.Row
                    key={estate.estateId}
                    style={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onClick={() => openDetailsModal(estate)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDetailsModal(estate);
                      }
                    }}
                    aria-label={`Abrir detalhes da imobiliária ${estate.estateName}`}
                  >
                    <Table.Cell>{formatActivatedAt(estate.activatedAt)}</Table.Cell>
                    <Table.Cell>{estate.estateName}</Table.Cell>
                    <Table.Cell>{estate.activeCollaboratorsCount}</Table.Cell>
                    <Table.Cell>{estate.paidChargesCount}</Table.Cell>
                    <Table.Cell>{currencyFormatter.format(estate.totalMovedAmount || 0)}</Table.Cell>
                    <Table.Cell>
                      {currencyFormatter.format(estate.ladoBomRevenueAmount || 0)}
                    </Table.Cell>
                    <Table.Cell>{billingEstateStatusLabelMap[estate.status]}</Table.Cell>
                    <Table.Cell textAlign="center" onClick={stopRowOpen}>
                      <EstatesChargesActionsMenu
                        estate={estate}
                        onViewDetails={openDetailsModal}
                        onShareBiometry={openBiometryModal}
                        onManageRegistration={openRegistration}
                        onRetryBiometry={openDetailsModal}
                        onViewCharges={openRegistration}
                        onEditBankAccount={
                          canEditBankAccount ? openDetailsModal : undefined
                        }
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </TableWrapper>

        {total > 0 && (
          <TablePaginationMenu
            currentPage={currentPage}
            dataCount={total}
            setCurrentPage={setCurrentPage}
          />
        )}
      </Segment>

      <EstateBillingDetailsModal
        open={detailsModalOpen}
        estateId={detailsEstateId}
        onClose={closeDetailsModal}
        onShareBiometry={openBiometryFromDetails}
        onManageRegistration={openRegistrationFromDetails}
        onViewCharges={openRegistrationFromDetails}
        onRetrySuccess={() => loadData(currentPage, appliedFilters, sort)}
        canEditBankAccount={canEditBankAccount}
      />

      <EstateBiometryModal
        open={biometryModalOpen}
        estate={selectedEstate}
        onClose={closeBiometryModal}
      />
    </>
  );
};

export default EstatesChargesList;
